import cookieParser from 'cookie';
import Cookie from 'js-cookie';
import moment from 'moment-timezone';

import { APP_DEFAULT_ADULTS, APP_DEFAULT_CHILDREN } from '@common/constants';
import { entitySelectors } from '@store/entity/selectors';
import * as yourJourneyActions from '@store/yourJourney/actions';
import { getMinimumStayToDate } from '@utils/date';
import { formatDate, parseDateToString } from '@utils/helpers';

const containsInteger = (string) => /^\+?(0|[1-9]\d*)$/.test(string);
const JSON_PARAMS = ['rooms', 'childAges'];

const isTodayOrGreater = (dateString) => moment(dateString).isSameOrAfter(moment().startOf('day'));

const getChildrenCountFromAges = (childAges) => {
  if (!childAges || typeof childAges !== 'object') {
    return 0;
  }
  return Object.keys(childAges).length;
};

const trimChildAges = (childAges, maxChildren) => {
  if (!childAges || typeof childAges !== 'object' || maxChildren <= 0) {
    return {};
  }

  const entries = Object.entries(childAges);
  const trimmedEntries = entries.slice(0, maxChildren);
  return Object.fromEntries(trimmedEntries);
};

const sanitizeJourneyParams = (rawObject) => {
  const sanitizedData = {};
  if (rawObject.arrivalDate) {
    sanitizedData.fromDate = isTodayOrGreater(rawObject.arrivalDate)
      ? parseDateToString(rawObject.arrivalDate)
      : parseDateToString(moment());
    sanitizedData.spaDate = parseDateToString(rawObject.arrivalDate);
  }
  if (rawObject.spaDate && isTodayOrGreater(rawObject.spaDate)) {
    sanitizedData.spaDate = parseDateToString(rawObject.spaDate);
  }
  if (rawObject.fromDate) {
    sanitizedData.fromDate = isTodayOrGreater(rawObject.fromDate)
      ? parseDateToString(rawObject.fromDate)
      : parseDateToString(moment());
  }
  if (rawObject.toDate) {
    sanitizedData.toDate = isTodayOrGreater(rawObject.toDate)
      ? parseDateToString(rawObject.toDate)
      : parseDateToString(moment().add(1, 'day'));
  }
  if (rawObject.adults?.toString() && containsInteger(rawObject.adults)) {
    sanitizedData.adults = parseInt(rawObject.adults, 10);
  }
  const rawSpaAdults = rawObject.spaAdults ?? rawObject.guests;
  if (rawSpaAdults?.toString() && containsInteger(rawSpaAdults)) {
    sanitizedData.spaAdults = parseInt(rawSpaAdults, 10);
  }
  if (rawObject.infants?.toString() && containsInteger(rawObject.infants)) {
    sanitizedData.children = parseInt(rawObject.infants, 10);
  }
  if (rawObject.children?.toString() && containsInteger(rawObject.children)) {
    sanitizedData.children = parseInt(rawObject.children, 10);
  }
  if (rawObject.promoRateCode) {
    sanitizedData.promoRateCode = rawObject.promoRateCode.toString();
  }
  if (rawObject.tableAdults?.toString() && containsInteger(rawObject.tableAdults)) {
    sanitizedData.tableAdults = parseInt(rawObject.tableAdults, 10);
  }
  if (rawObject.tableChildren?.toString() && containsInteger(rawObject.tableChildren)) {
    sanitizedData.tableChildren = parseInt(rawObject.tableChildren, 10);
  }
  if (rawObject.childAges) {
    try {
      sanitizedData.childAges = JSON.parse(rawObject.childAges);
    } catch {
      sanitizedData.childAges = {};
    }
  }
  try {
    if (rawObject.rooms) {
      sanitizedData.rooms = Array.isArray(rawObject.rooms)
        ? rawObject.rooms
        : JSON.parse(rawObject.rooms);
    }
    if (sanitizedData.rooms.length === 1 && rawObject.adults) {
      sanitizedData.rooms[0].adults = parseInt(rawObject.adults, 10);
    }
    if (rawObject.children || rawObject.infants) {
      const childrenCount = rawObject.children || rawObject.infants || 0;
      sanitizedData.rooms[0].children = parseInt(childrenCount, 10);
      sanitizedData.children = parseInt(childrenCount, 10);

      const existingChildrenCount = sanitizedData.children || 0;
      const childrenFromAges = getChildrenCountFromAges(sanitizedData.childAges);
      if (childrenFromAges > existingChildrenCount) {
        sanitizedData.childAges = trimChildAges(sanitizedData.childAges, existingChildrenCount);
      }
    }
  } catch {
    sanitizedData.rooms = [
      {
        roomNumber: 1,
        adults: APP_DEFAULT_ADULTS,
        children: APP_DEFAULT_CHILDREN,
      },
    ];
  }
  return sanitizedData;
};

const setJourneyParams = (ctx, defaultPropertyPartySize, store, property) => {
  const isServer = !!ctx.req;
  // Use property passed directly, fall back to store if available
  if (!property) {
    property = entitySelectors.getProperty(store.getState());
  }
  const applicationDefaultPartySize = {
    adults: APP_DEFAULT_ADULTS,
    childAges: '{}',
  };

  defaultPropertyPartySize = {
    ...defaultPropertyPartySize,
    rooms: [
      {
        roomNumber: 1,
        adults: defaultPropertyPartySize?.adults || APP_DEFAULT_ADULTS,
        children: defaultPropertyPartySize?.children || APP_DEFAULT_CHILDREN,
      },
    ],
  };
  const cookies = cookieParser.parse(
    isServer ? ctx.req.headers.cookie || '' : document.cookie || ''
  );

  const queryParams = ctx.query;

  const storedJourneyParams = sanitizeJourneyParams({ ...cookies, ...queryParams });

  const journeyParams = {
    fromDate: formatDate(moment().tz(property.timezone)),
    ...applicationDefaultPartySize,
    ...defaultPropertyPartySize,
    ...storedJourneyParams,
  };

  journeyParams.toDate = formatDate(
    getMinimumStayToDate(
      moment(journeyParams.fromDate),
      journeyParams.toDate ? moment(journeyParams.toDate) : undefined
    )
  );

  if (!journeyParams.spaDate) {
    journeyParams.spaDate = journeyParams.fromDate;
  }

  const journeyParamsServerCookie = [];

  Object.entries(journeyParams).forEach(([key, value]) => {
    if (!value) {
      return;
    }

    const parsedValue = yourJourneyActions.DATE_PARAMS.includes(key)
      ? value
      : JSON_PARAMS.includes(key)
      ? JSON.stringify(value)
      : value.toString();
    if (isServer) {
      journeyParamsServerCookie.push(
        cookieParser.serialize(key, parsedValue, {
          path: '/',
          secure: process.env.NODE_ENV === 'production',
          sameSite: 'lax',
        })
      );
    } else {
      Cookie.set(key, parsedValue, {
        path: '/',
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'lax',
      });
    }
  });
  if (isServer) {
    ctx.res.setHeader('Set-Cookie', journeyParamsServerCookie);
  }

  const {
    fromDate,
    toDate,
    spaDate,
    adults,
    children,
    spaAdults,
    promoRateCode,
    tableAdults,
    tableChildren,
    childAges,
    rooms,
  } = journeyParams;

  store.dispatch(
    yourJourneyActions.setJourneyParams({
      fromDate,
      toDate,
      spaDate,
      adults,
      children,
      spaAdults,
      promoRateCode,
      tableAdults,
      tableChildren,
      childAges,
      rooms,
    })
  );
};

export default setJourneyParams;
